"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.settingsSearchHitSchema = exports.SETTINGS_SEARCH_KINDS = exports.SETTINGS_SEARCH_AREAS = exports.SEARCH_LIMIT_PER_KIND = exports.SEARCH_MIN_QUERY = exports.searchResponseSchema = exports.searchHitSchema = exports.SEARCH_KINDS = void 0;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Global search — item 71. One shape for six kinds of thing.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE FLAT LIST, NOT SIX TYPED ARRAYS.** The palette renders rows, ranks them together and
 * arrows through them as one sequence; a `{ tasks: [], projects: [], … }` response would make the
 * client flatten it on arrival and then invent an order — which is a ranking decision taken in the
 * place that knows least about it. The server groups by `kind`, and the client groups by `kind`
 * for display, from one ordered list.
 *
 * ⚠️ **`href` is composed by the SERVER, and it is workspace-relative.** The alternative is a
 * client switch over `kind` — six string templates that drift the first time a route moves, on the
 * one screen where a wrong link looks exactly like a right one until it 404s.
 */
exports.SEARCH_KINDS = [
    'task',
    'project',
    'person',
    'report',
    'contract',
    'line_item',
];
exports.searchHitSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.SEARCH_KINDS),
    id: zod_1.z.uuid(),
    /** What to show. A task's title, a person's display name — already the reader's own text. */
    label: zod_1.z.string(),
    /**
     * The second line: a task's key, a project's code, a person's email.
     *
     * ⚠️ **Nullable rather than optional**, because *"this kind has no hint"* and *"this row's hint
     * is missing"* are the same thing to a renderer and neither is worth a branch.
     */
    hint: zod_1.z.string().nullable(),
    /**
     * ⚠️ **Workspace-RELATIVE, with no leading segment.** `useWorkspaceNavigate` supplies the
     * workspace; a server that guessed one would be the second place that knows R-38's address
     * shape, and the first to be wrong after a rename.
     */
    href: zod_1.z.string(),
});
exports.searchResponseSchema = zod_1.z.object({ data: zod_1.z.array(exports.searchHitSchema) });
/**
 * ⚠️⚠️ **A FLOOR ON THE QUERY, AND IT IS A PERFORMANCE DECISION RATHER THAN A UX ONE.** A single
 * character matches most of a workspace across six tables, six `ilike '%x%'` scans deep, on every
 * keystroke — so the endpoint refuses it and the palette does not ask. Two is the shortest thing
 * anybody actually searches for (`QA`, a `PR` prefix).
 */
exports.SEARCH_MIN_QUERY = 2;
/**
 * ⚠️ **Per KIND, not overall.** Five tasks and five people is a useful palette; ten results that
 * all happen to be tasks is a task list somebody has to scroll past to reach the person they were
 * looking for.
 */
exports.SEARCH_LIMIT_PER_KIND = 5;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · THE SETTINGS SEARCH — what the two settings areas hold.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner: the search in both settings areas works like the main one, in the palette,
 * and finds *"all headers of all settings, and all created entities inside settings too"*. The
 * headings are the screen's own words (the client's index); the ENTITIES are rows only the server
 * can read, permission-trimmed, so they come from `GET /search/settings`.
 *
 * ⚠️ `workspace` is the workspace's settings (`settings.view`; roles need `settings.manage`);
 * `account` is the reader's own — their tokens, passkeys and skills.
 */
exports.SETTINGS_SEARCH_AREAS = ['workspace', 'account'];
exports.SETTINGS_SEARCH_KINDS = [
    'status',
    'priority',
    'skill',
    'skillCategory',
    'skillLevel',
    'schedule',
    'rateCard',
    'currency',
    'leaveType',
    'overtimeType',
    'holiday',
    'metafield',
    'projectRole',
    'role',
    'integration',
    'token',
    'passkey',
    'mySkill',
];
exports.settingsSearchHitSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.SETTINGS_SEARCH_KINDS),
    id: zod_1.z.uuid(),
    label: zod_1.z.string(),
    hint: zod_1.z.string().nullable(),
    /** Workspace-relative, like `searchHitSchema`'s: the section, or the entity's own page. */
    href: zod_1.z.string(),
});
//# sourceMappingURL=contract.js.map